import { cn, tv } from '@pcln/horizon'

// Car form variant definitions
const carFormVariants = {
  differentDropOff: {
    gridContainer:
      'grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] lg:grid-cols-[minmax(0,0.4fr)_minmax(0,0.4fr)_minmax(0,0.3fr)_minmax(0,0.3fr)_minmax(0,0.3fr)_minmax(0,0.3fr)_max-content]',
    pickupInput: 'col-span-full lg:[grid-area:unset]',
    dualPickupInput: 'col-span-2 lg:[grid-area:unset]',
    dropoffInput: 'col-span-2 lg:[grid-area:unset]',
    packageInput:
      'flex flex-row items-center justify-start gap-4 text-sm sm:col-span-2 col-span-full lg:order-1',
    submitInput: 'col-span-2 lg:[grid-area:unset]'
  },
  sameDropOff: {
    gridContainer:
      'grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] lg:grid-cols-[minmax(0,0.8fr)_minmax(0,0.4fr)_minmax(0,0.3fr)_minmax(0,0.4fr)_minmax(0,0.3fr)_max-content]',
    pickupInput: 'col-span-full lg:[grid-area:unset]',
    dualPickupInput: 'col-span-2 lg:[grid-area:unset]',
    dropoffInput: 'col-span-2 lg:[grid-area:unset]',
    packageInput:
      'flex flex-row items-center justify-start gap-4 text-sm sm:col-span-2 col-span-full lg:order-1',
    submitInput: 'col-span-2 lg:[grid-area:unset]'
  },
  hotelOnly: {
    gridContainer:
      'grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_max-content]',
    pickupInput: 'col-span-full lg:[grid-area:unset]',
    dualPickupInput: 'col-span-2 lg:[grid-area:unset]',
    dropoffInput: 'col-span-2 lg:[grid-area:unset]',
    packageInput:
      'flex flex-row items-center justify-start gap-4 text-sm sm:col-span-2 col-span-full lg:order-1',
    submitInput: 'col-span-2 lg:[grid-area:unset]'
  },
  withFlight: {
    gridContainer:
      'grid-cols-2 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_minmax(0,0.7fr)_max-content]',
    pickupInput: 'col-span-full lg:[grid-area:unset]',
    dualPickupInput: 'col-span-2 lg:[grid-area:unset]',
    dropoffInput: 'col-span-2 lg:[grid-area:unset]',
    packageInput:
      'flex flex-row items-center justify-start gap-4 text-sm sm:col-span-2 col-span-full lg:order-1',
    submitInput: 'col-span-2 lg:[grid-area:unset]'
  },
  // Compact variants - maintain sm layout through lg for homepage hero
  differentDropOffCompact: {
    gridContainer:
      'grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]',
    pickupInput: 'col-span-full',
    dualPickupInput: 'col-span-2',
    dropoffInput: 'col-span-2',
    packageInput: 'text-sm sm:col-span-2 col-span-full',
    submitInput: 'col-span-2'
  },
  sameDropOffCompact: {
    gridContainer:
      'grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]',
    pickupInput: 'col-span-full',
    dualPickupInput: 'col-span-2',
    dropoffInput: 'col-span-2',
    packageInput: 'text-sm sm:col-span-2 col-span-full',
    submitInput: 'col-span-2'
  },
  hotelOnlyCompact: {
    gridContainer: 'grid-cols-2',
    pickupInput: 'col-span-full',
    dualPickupInput: 'col-span-2',
    dropoffInput: 'col-span-2',
    packageInput: 'text-sm col-span-full sm:col-span-1',
    submitInput: 'col-span-full sm:col-span-1'
  },
  withFlightCompact: {
    gridContainer: 'grid-cols-2',
    pickupInput: 'col-span-full',
    dualPickupInput: 'col-span-2',
    dropoffInput: 'col-span-2',
    packageInput: 'text-sm col-span-full sm:col-span-1',
    submitInput: 'col-span-full sm:col-span-1'
  }
} as const

type CarTypeVariant = keyof typeof carFormVariants

const carFormSlots = {
  gridContainer: 'grid grid-cols-1 gap-3 items-center mx-auto',
  optionsWrapper: 'col-span-full',
  pickupInput: '',
  dualPickupInput: '',
  dropoffInput: '',
  packageInput: '',
  submitInput: '',
  // Date picker wrapper with translate fix
  datePickerWrapper: '**:translate-y-0',
  // Package date picker wrapper - stacks on mobile, side-by-side on sm+
  packageDatePickerWrapper: '**:translate-y-0 col-span-full sm:col-span-1',
  // Flight input slots
  flightInput: 'col-span-full sm:[grid-area:unset]'
} as const

export const horizonCarForm = tv({
  slots: carFormSlots,
  variants: { carType: carFormVariants },
  defaultVariants: { carType: 'sameDropOff' }
})

// Helper to determine variant based on form state
const getCarVariant = (
  isDifferentDropOff: boolean,
  isHotel: boolean,
  isFlight: boolean,
  useCompactLayout: boolean
): CarTypeVariant => {
  if (useCompactLayout) {
    if (isFlight) return 'withFlightCompact'
    if (isHotel) return 'hotelOnlyCompact'
    if (isDifferentDropOff) return 'differentDropOffCompact'
    return 'sameDropOffCompact'
  }

  if (isFlight) return 'withFlight'
  if (isHotel) return 'hotelOnly'
  if (isDifferentDropOff) return 'differentDropOff'
  return 'sameDropOff'
}

// Utility functions
export const getGridContainerClass = (
  isDifferentDropOff = false,
  isHotel = false,
  isFlight = false,
  useCompactLayout = false
) => {
  const variant = getCarVariant(
    isDifferentDropOff,
    isHotel,
    isFlight,
    useCompactLayout
  )
  const styles = horizonCarForm({ carType: variant })
  return cn(styles.gridContainer())
}

export const getInputClass = (
  type:
    | 'options'
    | 'pickup'
    | 'dropoff'
    | 'package'
    | 'submit'
    | 'datePickerWrapper'
    | 'packageDatePickerWrapper'
    | 'flightInput',
  isDifferentDropOff = false,
  isHotel = false,
  isFlight = false,
  useCompactLayout = false
) => {
  // Static slots that don't depend on variant
  if (type === 'datePickerWrapper') return carFormSlots.datePickerWrapper
  if (type === 'packageDatePickerWrapper')
    return carFormSlots.packageDatePickerWrapper
  if (type === 'flightInput') return carFormSlots.flightInput

  const variant = getCarVariant(
    isDifferentDropOff,
    isHotel,
    isFlight,
    useCompactLayout
  )
  const styles = horizonCarForm({ carType: variant })
  switch (type) {
    case 'pickup':
      return isDifferentDropOff
        ? styles.dualPickupInput()
        : styles.pickupInput()
    case 'options':
      return styles.optionsWrapper()
    case 'dropoff':
      return styles.dropoffInput()
    case 'package':
      return styles.packageInput()
    case 'submit':
      return styles.submitInput()
    default:
      return ''
  }
}
